import {
	type Action,
	type ActionScopeMap,
	getScopesFor,
	type PermissionScope,
	type permissionManifest,
} from "@msuite/citadel";
import { Select, type SelectOption } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { PermissionScopeTag, type PermissionScopeTagValue } from "@msuite/ui/tag";
import { Tooltip } from "@msuite/ui/tooltip";
import type { TranslationKey } from "@msuite/utils/locales";
import type { UserPermissionFormSchema } from "@msuite/utils/schemas";
import { type Path, useFormContext, useWatch } from "react-hook-form";
import { grantKey, scopeExplanationKey } from "@/components/citadel";
import { type RoleSource, unionContains } from "./permission-form-values";

/** The label of each scope, in the fixed order the scope map offers them. */
const SCOPE_LABELS: Record<PermissionScope, TranslationKey> = {
	organization: "roles.scope_organization",
	own: "roles.scope_own",
	ownProjects: "roles.scope_own_projects",
};

/**
 * What one option renders: its reach tag, and — inside the open list only — its provenance
 * and the explanation of what the reach grants.
 */
interface ScopeOptionItem {
	tag: PermissionScopeTagValue;
	suffix?: string;
	explanation: string;
}

/** Props Interface */
interface UserPermissionScopeCellProps {
	item: { id: string } & (typeof permissionManifest)[number];
	action: Action;
	/** The user holds this pair under a conditions template the scope map does not know. */
	isCustom: boolean;
	/** The roles granting this pair, with their reaches. */
	sources: RoleSource[];
}

/**
 * One cell of the individual permission grid, showing the **effective** reach. The roles'
 * combined reach is pre-selected; every option it contains is disabled — a disabled option
 * cannot promise a revocation this additive model does not have — and picking a wider or
 * sideways reach stores it as the person's individual extension. Picking the pre-selection
 * again removes the extension. The open list annotates the roles' reaches with the roles that
 * grant them; an amber dot beside the cell marks an individual extension.
 */
export function UserPermissionScopeCell({
	item,
	action,
	isCustom,
	sources,
}: UserPermissionScopeCellProps) {
	/** Context */
	const { t } = useTranslation();
	const { control } = useFormContext<UserPermissionFormSchema>();

	/** Computed */
	const name = `permissions.${grantKey(action, item.subject)}` as Path<UserPermissionFormSchema>;
	const value = useWatch({ control, name });
	const subject = item.subject as keyof typeof ActionScopeMap;
	const scopes = getScopesFor(subject, action);
	const explain = (tag: PermissionScopeTagValue) => t(scopeExplanationKey(subject, tag));

	// The roles' combined reach. `organization` collapses the rest; two entries appear only for
	// the subjects offering both narrow reaches, granted by different roles.
	const distinct = sources
		.map((source) => source.scope)
		.filter((scope, index, all): scope is PermissionScope =>
			Boolean(scope && all.indexOf(scope) === index),
		);
	const union = distinct.includes("organization")
		? (["organization"] as PermissionScope[])
		: distinct;
	const isMixed = union.length > 1;

	const roleNames = [...new Set(sources.filter(({ scope }) => scope).map(({ name: n }) => n))];
	const rolesGranting = (scope: PermissionScope) =>
		sources
			.filter((source) => source.scope === scope)
			.map((source) => source.name)
			.join(", ");
	// Roles granting the pair under a template the scope map cannot read. They contribute
	// nothing to the union — incomparable, so they must not disable anything — but the cell
	// still has to say the pair is covered rather than claim "no access".
	const customCoverageNames = [
		...new Set(sources.filter(({ scope }) => scope === null).map(({ name: n }) => n)),
	];

	// The carrier of `false`: with no coverage it is plain "no access"; with coverage it renders
	// the roles' reach — the pre-selection — and picking it means "no individual extension".
	// Its provenance names only the roles granting the shown reach; narrower granters appear on
	// their own disabled options. The full role list is spelled out and truncates with the
	// line; the open list follows the column's width, so widening the column is how a long
	// list is read in full.
	const primary = union[0];
	const base: ScopeOptionItem =
		primary === undefined
			? customCoverageNames.length
				? {
						tag: "custom",
						suffix: t("permissions.from_roles", { roles: customCoverageNames.join(", ") }),
						explanation: explain("custom"),
					}
				: { tag: "none", explanation: explain("none") }
			: {
					tag: isMixed ? "combined" : primary,
					suffix: t("permissions.from_roles", {
						roles: isMixed ? roleNames.join(", ") : rolesGranting(primary),
					}),
					explanation: explain(isMixed ? "combined" : primary),
				};
	const baseLabel =
		primary === undefined
			? t(customCoverageNames.length ? "roles.scope_custom" : "roles.scope_none")
			: isMixed
				? t("roles.scope_combined")
				: t(SCOPE_LABELS[primary]);

	const options: Array<SelectOption<ScopeOptionItem, false | PermissionScope>> = isCustom
		? [
				{
					value: false,
					label: t("roles.scope_custom"),
					item: { tag: "custom", explanation: explain("custom") },
				},
			]
		: [
				{
					value: false,
					label: baseLabel,
					item: base,
				},
				...scopes
					// A single covered reach is represented by the carrier above; listing it again
					// would put the same level in the list twice.
					.filter((scope) => !(union.length === 1 && union[0] === scope))
					.map((scope) => ({
						value: scope as false | PermissionScope,
						label: t(SCOPE_LABELS[scope]),
						item: {
							tag: scope as PermissionScopeTagValue,
							suffix: union.includes(scope)
								? t("permissions.from_roles", { roles: rolesGranting(scope) })
								: undefined,
							explanation: explain(scope),
						},
						isDisabled: unionContains(union, scope),
					})),
			];

	const isExtended = !isCustom && value !== false && value !== undefined;
	const coverage = sources
		.map(
			(source) =>
				`${source.name} (${t(source.scope ? SCOPE_LABELS[source.scope] : "roles.scope_custom")})`,
		)
		.join(", ");
	const markerLabel = coverage
		? `${t("permissions.individually_extended")} — ${t("permissions.covered_by_roles", { roles: coverage })}`
		: t("permissions.individually_extended");

	/** Guard */
	// A subject that does not offer the action has no cell at all, rather than a select whose
	// only answer is "no access" — the pair cannot be granted in the first place.
	if (!item.actions.includes(action)) return null;

	/** Render */
	return (
		<div className="px-2 w-full items-center gap-1.5 h-stack">
			<div className="flex-1 min-w-0">
				<Select
					control={control}
					name={name}
					withBackdrop
					label={undefined}
					isRequired={false}
					isNullable={false}
					isDisabled={isCustom}
					className="py-1 rounded-sm"
					data-cy={`user-scope-select-${action}-${item.subject}`}
					options={options}
					// Wider than the narrow cell so the explanations read as sentences, not shreds.
					optionsClassName="min-w-72"
					renderOption={(option) => (
						<div className="min-w-0 v-stack gap-1!">
							<div className="items-center gap-1.5 h-stack min-w-0">
								<PermissionScopeTag scope={option.tag} />
								{/* Provenance renders only inside the open list; the closed button shows
								    nothing but the reach tag. */}
								{option.suffix && (
									<p className="hidden group-[.listbox-option]:block opacity-50 text-xs truncate">
										{option.suffix}
									</p>
								)}
							</div>
							{/* The explanation follows the provenance's rule: open list only. */}
							<p className="hidden group-[.listbox-option]:block opacity-50 text-xs whitespace-normal">
								{option.explanation}
							</p>
						</div>
					)}
				/>
			</div>
			{/* An individual extension beyond the roles — the rarer fact, so it gets the marker.
			    The slot is always occupied, invisible without an extension, so a dot appearing
			    never shifts the select's width. */}
			{isExtended ? (
				<Tooltip
					label={markerLabel}
					className="cursor-pointer"
				>
					<div className="shrink-0 p-1">
						<StatusIndicator color="amber" />
					</div>
				</Tooltip>
			) : (
				<div
					className="shrink-0 p-1 opacity-0"
					aria-hidden
				>
					<StatusIndicator color="amber" />
				</div>
			)}
		</div>
	);
}
